// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import DisablerContext from "@insite/mobius/utilities/DisablerContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Checkmark from "@store/Library/Components/Toolkit/Icons/Checkmark";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { useStepperContextStep } from "@store/Framework/Components/StepperContext";
import EditFill from "@store/Library/Components/Toolkit/Icons/Edit/EditFill";
import { linkGetActionLinkProps } from "@store/Themes/common/props/link";
import { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import expandProps from "@toolkit/Framework/Common/Utilities/expandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { EventHandler, FC, HTMLAttributes, PropsWithChildren } from "react";
import { css } from "styled-components";

const maxStepCount = 10; // This is used for z-index stacking of the steps. If more than 10 steps are needed, this will need to be updated.

export interface FormStepPresentationProps extends ExpandStyles<FormStepStyles> {}

export interface FormStepProps extends FormStepPresentationProps, Omit<HTMLAttributes<HTMLElement>, "onSubmit"> {
    contextStepNumber?: number;
    onCancel?: EventHandler<React.MouseEvent>;
    onSubmit?: EventHandler<React.MouseEvent>;
    onPrevious?: EventHandler<React.MouseEvent>;
    title: string;
    titleValue?: string;
}

export interface FormStepStyles {
    actionsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    completeIcon?: IconPresentationProps;
    content?: InjectableCss;
    editLink?: LinkPresentationProps;
    header?: InjectableCss;
    nextButton?: ButtonPresentationProps;
    number?: TypographyPresentationProps;
    previousButton?: ButtonPresentationProps;
    saveButton?: ButtonPresentationProps;
    title?: TypographyPresentationProps;
    wrapper?: InjectableCss;
}

const overlayStyles = () => css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: -1px;
    border-radius: inherit;
`;

const borderOverlayStyles = () => css`
    position: relative;
    z-index: 0;

    &::before {
        ${overlayStyles()}
        content: "";
        z-index: -1;
        border: 2px solid var(--border-overlay-color);
        transition: inherit;
    }
`;

export const formStepStyles: FormStepStyles = {
    wrapper: {
        css: css`
            --border-overlay-color: transparent;
            --wrapper-border-color: ${getColor("common.borderLight")};
            --header-text-color: ${getColor("main")};
            --number-box-color: ${getColor("common.borderLight")};
            --number-background-color: ${getColor("common.accent")};
            --number-text-color: inherit;

            ${borderOverlayStyles()}
            position: relative;
            min-width: 0; /* required to allow childrens flex overflow within the step */
            padding: ${pxToRem(23)};
            background-color: ${getColor("common.background")};
            border: 1px solid var(--wrapper-border-color);
            border-radius: ${pxToRem(8)};
            transition: ${transitionHoverStyles("border-color")};

            & + .form-step {
                margin-top: ${pxToRem(16)};
            }

            ${() => {
                let result = "";

                for (let i = 1; i <= maxStepCount; i++) {
                    result += `&:nth-child(${i}) { z-index: ${maxStepCount - i}; }`;
                }

                return result;
            }}

            &.active {
                --border-overlay-color: ${getColor("primary")};
                --wrapper-border-color: ${getColor("primary")};
                --header-text-color: ${getColor("primary")};
                --number-box-color: ${getColor("primary")};
                --number-background-color: ${getColor("primary.lightest")};
            }

            &.complete {
                --number-box-color: ${getColor("primary")};
                --number-background-color: ${getColor("primary")};
                --number-text-color: ${getColor("invert")};
            }

            &.pending {
                --wrapper-border-color: transparent;
                --header-text-color: ${getColor("text.lighter")};

                padding: ${pxToRem(15, 23)};
                background-color: ${getColor("common.accent")};
            }

            &.disabled:not(.pending) {
                &::after {
                    ${overlayStyles()}
                    content: "";
                    background-color: ${getColor("common.background")};
                    opacity: 0.6;
                    z-index: 1;
                }
            }
        `,
    },
    header: {
        css: css`
            float: left; /* Removes default legend tag position */
            padding: 0; /* Removes default legend tag padding */
            ${spacingStyles(null, pxToRem(24))}
            display: flex;
            gap: ${pxToRem(8)};
            width: 100%;
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            color: var(--header-text-color);
            transition: ${transitionHoverStyles("color")};
        `,
    },
    number: {
        css: css`
            ${borderOverlayStyles()}
            display: flex;
            flex-shrink: 0;
            align-items: center;
            justify-content: center;
            width: ${pxToRem(28)};
            height: ${pxToRem(28)};
            font-size: ${x => x.theme.design.typography.size.base};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            color: var(--number-text-color);
            background-color: var(--number-background-color);
            border: 1px solid var(--number-box-color);
            border-radius: 50%;
            transition: ${transitionHoverStyles("color", "background-color", "border-color")};
        `,
    },
    completeIcon: {
        size: 16,
        src: Checkmark,
        css: css`
            color: var(--number-text-color);
        `,
    },
    title: {
        css: css`
            flex-grow: 1;
            margin-top: ${pxToRem(2)};
            font-size: ${x => x.theme.design.typography.size.large};
            font-weight: ${x => x.theme.design.typography.weight.bold};

            > span {
                font-weight: ${x => x.theme.design.typography.weight.base};
            }
        `,
    },
    editLink: mergeProps<LinkPresentationProps>(linkGetActionLinkProps(EditFill), {
        css: css`
            margin-top: ${pxToRem(4)};
        `,
    }),
    content: {
        css: css`
            display: flex;
            flex-direction: column;
            clear: left; /* Ensures the header stays on its own line */
        `,
    },
    actionsWrapper: {
        css: css`
            ${spacingStyles(pxToRem(24), null)}
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(8, 16)};

            ${breakpoint("xs", "down")} {
                > button {
                    flex-grow: 1;
                    flex-basis: 1%;
                }
            }
        `,
    },
    previousButton: { variant: "tertiary" },
    nextButton: { variant: "primary" },
    cancelButton: { variant: "tertiary" },
    saveButton: { variant: "primary" },
};

const FormStep: FC<PropsWithChildren<FormStepProps>> = ({
    children,
    contextStepNumber,
    extendedStyles,
    onCancel,
    onSubmit,
    onPrevious,
    overridedStyles,
    title,
    titleValue,
    ...htmlAttributes
}) => {
    const shellContext = useShellContext();
    const styles = expandProps(formStepStyles, extendedStyles, overridedStyles);

    const {
        editStep,
        isActive,
        isComplete,
        isDisabled,
        isEditing,
        isPending,
        nextStep,
        previousStep,
        saveStep,
        stepCount,
        stepNumber,
    } = useStepperContextStep(contextStepNumber);

    if (!stepNumber) {
        return null;
    }

    const handlePrevious: FormStepProps["onPrevious"] = event => {
        onPrevious?.(event);

        if (!event.defaultPrevented) {
            previousStep();
        }
    };

    const handleNext: FormStepProps["onSubmit"] = event => {
        onSubmit?.(event);

        if (!event.defaultPrevented) {
            nextStep();
        }
    };

    const handleCancel: FormStepProps["onCancel"] = event => {
        onCancel?.(event);

        if (!event.defaultPrevented) {
            saveStep();
        }
    };

    const handleSave: FormStepProps["onSubmit"] = event => {
        onSubmit?.(event);

        if (!event.defaultPrevented) {
            saveStep();
        }
    };

    const accessibilityTitle = translate("Step {0}: {1}", `${stepNumber}`, title);

    return (
        <StyledWrapper
            {...styles.wrapper}
            as="fieldset"
            {...htmlAttributes}
            className={`form-step${isActive ? " active" : ""}${isComplete ? " complete" : ""}${isDisabled ? " disabled" : ""}${isPending ? " pending" : ""}`}
        >
            <DisablerContext.Provider value={{ disable: isDisabled }}>
                <StyledWrapper as="legend" {...styles.header}>
                    <VisuallyHidden>
                        {titleValue ? (
                            <>
                                {translate("{0}:", accessibilityTitle)} {titleValue}
                            </>
                        ) : (
                            accessibilityTitle
                        )}
                    </VisuallyHidden>
                    <Typography {...styles.number} aria-hidden="true">
                        {isComplete ? <Icon {...styles.completeIcon} /> : stepNumber}
                    </Typography>
                    <Typography {...styles.title} aria-hidden="true">
                        {titleValue ? (
                            <>
                                {translate("{0}:", title)} <span>{titleValue}</span>
                            </>
                        ) : (
                            title
                        )}
                    </Typography>
                    {!shellContext.isEditing && !isActive && !isPending && (
                        <Link {...styles.editLink} onClick={editStep} type="button">
                            {translate("Edit")}
                        </Link>
                    )}
                </StyledWrapper>
                {Boolean(children) && !isPending && <StyledWrapper {...styles.content}>{children}</StyledWrapper>}
                {isActive && !isEditing && (
                    <StyledWrapper {...styles.actionsWrapper}>
                        {Boolean(stepNumber > 1) && (
                            <Button {...styles.previousButton} onClick={handlePrevious} type="button">
                                {translate("Back")}
                            </Button>
                        )}
                        <Button {...styles.nextButton} onClick={handleNext} type="button">
                            {stepNumber === stepCount ? translate("Save & Review") : translate("Next")}
                        </Button>
                    </StyledWrapper>
                )}
                {isActive && isEditing && (
                    <StyledWrapper {...styles.actionsWrapper}>
                        <Button {...styles.cancelButton} onClick={handleCancel} type="button">
                            {translate("Cancel")}
                        </Button>
                        <Button {...styles.saveButton} onClick={handleSave} type="button">
                            {translate("Save")}
                        </Button>
                    </StyledWrapper>
                )}
            </DisablerContext.Provider>
        </StyledWrapper>
    );
};

export default FormStep;
